import { FC, useState } from 'react';
import { addDays, addYears } from 'date-fns';

import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import { formatDate, P, PP } from 'helpers/date';

import locales from './locales';
import { DeactivationDatePickerProps } from './types';

const DELAY_IN_DAYS = [1, 7, 14, 30];
const TOMORROW = addDays(new Date(), 1);
const THREE_YEARS_FROM_NOW = addYears(new Date(), 3);

const computeDaysInDate = (days: number) => {
  return addDays(new Date(), days);
};

const getNumberOfDaysLocale = (numberOfDays: number): string => {
  if (numberOfDays === 1) {
    return locales.labels.tomorrow;
  }

  if (numberOfDays >= 30) {
    return locales.labels.dateInMonth;
  }

  return locales.formatString(
    locales.labels.dateInDays,
    numberOfDays,
  ) as string;
};

const DeactivationDatePicker: FC<DeactivationDatePickerProps> = ({
  chosenDate,
  deactivateAt,
  maxDate = THREE_YEARS_FROM_NOW,
  setChosenDate,
}) => {
  const [deactivationDays, setDeactivationDays] = useState<number>(0);
  const [isCustomDateSelected, setIsCustomDateSelected] =
    useState<boolean>(false);

  const onSelect = (newDeactivationDays: number) => {
    setDeactivationDays(newDeactivationDays);
    const selectedDate = computeDaysInDate(newDeactivationDays);
    setChosenDate(selectedDate);
    setIsCustomDateSelected(false);
  };

  return (
    <>
      <Heading marginBottom="space-16">{locales.cardDeactivationLabel}</Heading>

      <Text marginBottom="space-32">
        {deactivateAt
          ? locales.formatString(
              locales.temporaryCardDescription,
              formatDate(new Date(deactivateAt), PP),
            )
          : locales.permanentCardDescription}
      </Text>

      <SunshineCard.RadioGroup
        onChange={(e) => onSelect(Number(e))}
        value={
          isCustomDateSelected ? 'custom-date' : deactivationDays.toString()
        }
      >
        <SunshineCard.Group>
          {DELAY_IN_DAYS.map((option) => (
            <SunshineCard
              key={option}
              value={option.toString()}
              variant="radio"
            >
              <SunshineCard.Title>
                {getNumberOfDaysLocale(option)}
              </SunshineCard.Title>

              <SunshineCard.Text>
                {`${locales.labels.the}${formatDate(
                  addDays(new Date(), option),
                  P,
                )}`}
              </SunshineCard.Text>
            </SunshineCard>
          ))}

          <SunshineCard
            onClick={() => setIsCustomDateSelected(true)}
            value="custom-date"
            variant="radio"
          >
            <SunshineCard.Title>{locales.labels.the}</SunshineCard.Title>

            <Form.SunshineInputDatePicker
              modifiers={{
                disabled: {
                  after: maxDate,
                  before: TOMORROW,
                },
              }}
              onChange={(date) => (date ? setChosenDate(date) : null)}
              value={isCustomDateSelected ? chosenDate ?? undefined : undefined}
            />
          </SunshineCard>
        </SunshineCard.Group>
      </SunshineCard.RadioGroup>
    </>
  );
};

export default DeactivationDatePicker;
